// Touch controls (§8.3, §26): a floating (or fixed) move stick and context-morphing buttons, built
// from DOM elements with Pointer Events, `setPointerCapture` and per-pointer tracking, so several
// fingers work at once. Layout comes from `touchLayout.ts` (pure, unit-tested) and reads the touch
// settings (handedness, size, stick), the UI scale and the safe-area insets.
//
// Morphing (§8.3): BONK → PEBBLE as a ghost, with a cooldown ring; GRAB → THROW while carrying and
// MASH! (pulsing) while grabbed, when any tap anywhere also counts as a mash; JUMP → CLIMB on a
// ledge and HOP! while teetering; ITEM shows the icon and charges, pulses when held and hides when
// empty; EMOTE opens a 4-wedge radial (press-drag-release or tap-tap); PAUSE emits the pause nav.
// The layer sits first in the UI root, under every screen, so on-screen UI always wins its taps.
import { ItemKind } from '@shared/ids';
import { BS } from '@shared/ids';
import './touch.css';
import { EMOTE_ICONS, EMOTE_LABELS, ITEM_ICONS, ITEM_NAMES, TOUCH_ICONS, type TouchIconId } from './touchIcons';
import { computeTouchLayout, stickVector, type TouchButtonId, type TouchCircle, type TouchLayout } from './touchLayout';

export type TouchMode = 'hidden' | 'stick' | 'full';
export type TouchAction = 'shove' | 'grab' | 'jump' | 'item' | 'honk';

export interface TouchHooks {
  press(action: TouchAction): void;
  release(action: TouchAction): void;
  emote(id: number): void;
  pause(): void;
  /** Replay skip taps (§14.2 "Hold [Enter]/(A)/tap to skip"). */
  confirm(): void;
  /** Practice item wheel (§23.7). */
  item(kind: ItemKind): void;
  /** Any touch on the controls (device switching). */
  activity(): void;
}

export interface TouchOptions {
  layout: 'right' | 'left';
  buttonSize: 'S' | 'M' | 'L';
  stick: 'floating' | 'fixed';
  /** UI scale as a factor (0.8–1.4). */
  uiScale: number;
  reducedMotion: boolean;
}

export interface TouchContext {
  ghost: boolean;
  carrying: boolean;
  grabbed: boolean;
  item: ItemKind;
  charges: number;
  /** Local bean state (BS), or -1. */
  state: number;
  /** Remaining shove (or pebble, as a ghost) cooldown as a fraction 1 → 0. */
  cooldown: number;
  /** A replay is on screen: taps vote to skip. */
  replay: boolean;
  /** Practice: show the item wheel button. */
  itemWheel: boolean;
}

const SVG_NS = 'http://www.w3.org/2000/svg';
const RING_R = 46;
const RING_LEN = 2 * Math.PI * RING_R;
/** A press-drag on EMOTE / the item wheel selects by the swipe direction once it is this long. */
const RADIAL_SELECT_PX = 22;
/** Beyond this many stick radii the floating base follows the finger. */
const STICK_FOLLOW = 1.15;
const HUD_VARS: readonly string[] = [
  '--tc-dial-x', '--tc-dial-bottom', '--tc-dial-max-w', '--tc-top-left-x', '--tc-cluster-top', '--tc-cluster-x0', '--tc-cluster-x1',
  '--tc-stick-x0', '--tc-stick-x1', '--tc-stick-top', '--tc-top-row-x0', '--tc-top-row-top', '--tc-top-row-bottom',
];
/** The wheel hub closes it. */
const RADIAL_CLOSE = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 7l10 10M17 7 7 17" fill="none" stroke="#2A1A3A" stroke-width="3.4" stroke-linecap="round"/></svg>';
/** Four small chevrons on the stick base: "this moves". */
const STICK_TICKS = '<svg viewBox="0 0 100 100" aria-hidden="true">'
  + [0, 90, 180, 270].map((a) => `<path transform="rotate(${a} 50 50)" d="M42.5 19 50 11.5 57.5 19" fill="none" stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/>`).join('')
  + '</svg>';
const PRACTICE_ITEMS: readonly ItemKind[] = [ItemKind.BANANA, ItemKind.BOMB, ItemKind.GLOVE, ItemKind.BALLOON, ItemKind.PLUNGER, ItemKind.SHAKE];

interface ButtonEl {
  id: TouchButtonId;
  root: HTMLDivElement;
  disc: HTMLDivElement;
  icon: HTMLDivElement;
  label: HTMLDivElement;
  pointer: number | null;
  iconKey: string;
  labelText: string;
}

interface Radial {
  root: HTMLDivElement;
  plate: HTMLDivElement;
  items: HTMLDivElement[];
  /** The button that opens it (highlighted while open). */
  source: TouchButtonId;
  cx: number;
  cy: number;
  pick(i: number): void;
}

function div(cls: string, parent?: HTMLElement): HTMLDivElement {
  const d = document.createElement('div');
  d.className = cls;
  if (parent) parent.append(d);
  return d;
}

/** Safe-area insets in CSS px, read through a probe element. */
function readSafeInsets(probe: HTMLElement): { top: number; right: number; bottom: number; left: number } {
  const cs = getComputedStyle(probe);
  const px = (v: string): number => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  return { top: px(cs.paddingTop), right: px(cs.paddingRight), bottom: px(cs.paddingBottom), left: px(cs.paddingLeft) };
}

export class TouchControls {
  /** Current move vector from the stick (screen axes, |v| ≤ 1). */
  readonly move = { x: 0, y: 0 };
  private root: HTMLDivElement | null = null;
  private zone!: HTMLDivElement;
  private mash!: HTMLDivElement;
  private tap!: HTMLDivElement;
  private stick!: HTMLDivElement;
  private base!: HTMLDivElement;
  private knob!: HTMLDivElement;
  private dir!: HTMLDivElement;
  private probe!: HTMLDivElement;
  private ring!: SVGCircleElement;
  private badge!: HTMLDivElement;
  private shoveDisc!: HTMLDivElement;
  private lastSweep = '';
  private readonly buttons = new Map<TouchButtonId, ButtonEl>();
  private emoteRadial: Radial | null = null;
  private itemRadial: Radial | null = null;
  private openRadial: Radial | null = null;
  private radialPointer: number | null = null;
  private radialSticky = false;
  private radialHover = -1;
  /** Where the finger went down on the radial's button (swipe origin). */
  private readonly radialPress = { x: 0, y: 0 };
  private stickPointer: number | null = null;
  private readonly stickOrigin = { x: 0, y: 0 };
  private readonly held = new Set<TouchAction>();
  private modeValue: TouchMode = 'hidden';
  private layoutValue: TouchLayout | null = null;
  private opts: TouchOptions = { layout: 'right', buttonSize: 'M', stick: 'floating', uiScale: 1, reducedMotion: false };
  private ctx: TouchContext = { ghost: false, carrying: false, grabbed: false, item: ItemKind.NONE, charges: 0, state: -1, cooldown: 0, replay: false, itemWheel: false };
  private lastCooldown = 0;
  private lastItem: ItemKind = ItemKind.NONE;
  private readonly abort = new AbortController();
  private killFeedHeight: number | undefined;
  private overlay: HTMLElement | null = null;

  constructor(private readonly hooks: TouchHooks) {}

  get mode(): TouchMode {
    return this.modeValue;
  }

  get layout(): TouchLayout | null {
    return this.modeValue === 'hidden' ? null : this.layoutValue;
  }

  /** True while the stick is deflected past its deadzone (touch throw aim, §8.3). */
  get swiping(): boolean {
    return this.stickPointer !== null && (this.move.x !== 0 || this.move.y !== 0);
  }

  isHeld(a: TouchAction): boolean {
    return this.held.has(a);
  }

  attach(overlay: HTMLElement): void {
    if (this.root) return;
    const root = document.createElement('div');
    root.id = 'touch-controls';
    root.className = 'tc-root';
    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    this.root = root;
    this.probe = div('tc-probe', root);
    this.mash = div('tc-catch tc-mash', root);
    this.tap = div('tc-catch tc-tap', root);
    this.zone = div('tc-zone', root);
    this.stick = div('tc-stick idle', root);
    this.base = div('tc-base', this.stick);
    div('tc-ticks', this.base).innerHTML = STICK_TICKS;
    this.dir = div('tc-dir', this.stick);
    this.knob = div('tc-knob', this.stick);
    for (const id of ['shove', 'jump', 'grab', 'item', 'honk', 'emote', 'pause', 'items'] as TouchButtonId[]) this.buttons.set(id, this.makeButton(id, root));
    this.emoteRadial = this.makeRadial(root, 'emote', 'tc-radial-emote', 'EMOTE', EMOTE_ICONS.map((k) => TOUCH_ICONS[k]), EMOTE_LABELS, (i) => this.hooks.emote(i + 1));
    this.itemRadial = this.makeRadial(root, 'items', 'tc-radial-items', 'PRACTICE ITEM', PRACTICE_ITEMS.map((k) => ITEM_ICONS[k]), PRACTICE_ITEMS.map((k) => ITEM_NAMES[k].split(' ').pop()!.toUpperCase()), (i) => this.hooks.item(PRACTICE_ITEMS[i]));
    overlay.prepend(root);
    this.overlay = overlay;

    const signal = this.abort.signal;
    this.zone.addEventListener('pointerdown', (e) => this.onStickDown(e), { signal });
    this.zone.addEventListener('pointermove', (e) => this.onStickMove(e), { signal });
    for (const t of ['pointerup', 'pointercancel', 'lostpointercapture'] as const) this.zone.addEventListener(t, (e) => this.onStickUp(e), { signal });
    this.mash.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.hooks.activity();
      if (this.ctx.grabbed) {
        this.hooks.press('grab');
        this.hooks.release('grab');
        this.burst(e.clientX, e.clientY);
      }
    }, { signal });
    this.tap.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.hooks.activity();
      this.hooks.confirm();
      this.burst(e.clientX, e.clientY);
    }, { signal });
    root.addEventListener('contextmenu', (e) => e.preventDefault(), { signal });
    window.addEventListener('resize', () => this.relayout(), { signal });
    window.addEventListener('orientationchange', () => this.relayout(), { signal });
    window.addEventListener('blur', () => this.releaseAll(), { signal });
    this.relayout();
  }

  detach(): void {
    this.abort.abort();
    this.root?.remove();
    this.root = null;
    this.publishHud();
  }

  setMode(mode: TouchMode): void {
    if (mode === this.modeValue) return;
    this.modeValue = mode;
    if (mode !== 'full') this.closeRadial();
    if (mode === 'hidden') this.releaseAll();
    if (!this.root) return;
    this.root.hidden = mode === 'hidden';
    this.root.dataset.mode = mode;
    if (mode !== 'hidden') this.relayout();
    else this.publishHud();
  }

  setOptions(o: TouchOptions): void {
    const changed = o.layout !== this.opts.layout || o.buttonSize !== this.opts.buttonSize || o.stick !== this.opts.stick || o.uiScale !== this.opts.uiScale;
    this.opts = { ...o };
    this.root?.classList.toggle('tc-reduced', o.reducedMotion);
    if (changed) this.relayout();
  }

  /** The HUD reports its kill-feed height so HONK/EMOTE sit right below it. */
  setKillFeedHeight(px: number | undefined): void {
    if (px === this.killFeedHeight) return;
    this.killFeedHeight = px;
    this.relayout();
  }

  setContext(c: TouchContext): void {
    this.ctx = c;
  }

  /** Per-frame visual state (diffed DOM writes). */
  frame(): void {
    const root = this.root;
    if (!root || this.modeValue === 'hidden') return;
    const c = this.ctx;
    const full = this.modeValue === 'full';
    toggle(root, 'tc-ghost', c.ghost);
    toggle(root, 'tc-grabbed', full && c.grabbed);
    toggle(root, 'tc-replay', c.replay);
    if (root.classList.contains('tc-wheel') !== (full && c.itemWheel && !c.replay)) {
      toggle(root, 'tc-wheel', full && c.itemWheel && !c.replay);
      this.publishHud();
    }
    if (!c.itemWheel && this.openRadial === this.itemRadial) this.closeRadial();

    const shove = this.buttons.get('shove')!;
    this.morph(shove, c.ghost ? 'pebble' : 'bonk', c.ghost ? 'PEBBLE' : 'BONK');
    const cd = Math.max(0, Math.min(1, c.cooldown));
    const offset = (RING_LEN * (1 - cd)).toFixed(1);
    if (this.ring.getAttribute('stroke-dashoffset') !== offset) this.ring.setAttribute('stroke-dashoffset', offset);
    const sweep = `${(cd * 360).toFixed(0)}deg`;
    if (sweep !== this.lastSweep) {
      this.lastSweep = sweep;
      this.shoveDisc.style.setProperty('--cd', sweep);
    }
    toggle(shove.root, 'cooling', cd > 0);
    if (this.lastCooldown > 0 && cd === 0) this.flash(shove.root, 'ready');
    this.lastCooldown = cd;

    const grab = this.buttons.get('grab')!;
    const grabState = c.grabbed ? 'mash' : c.carrying ? 'throw' : 'grab';
    this.morph(grab, grabState, grabState === 'mash' ? 'MASH!' : grabState === 'throw' ? 'THROW' : 'GRAB');
    if (grab.root.dataset.state !== grabState) grab.root.dataset.state = grabState;

    const jump = this.buttons.get('jump')!;
    const jumpState = c.state === BS.LEDGE ? 'climb' : c.state === BS.TEETER ? 'hop' : 'jump';
    this.morph(jump, jumpState === 'climb' ? 'climb' : 'jump', jumpState === 'climb' ? 'CLIMB' : jumpState === 'hop' ? 'HOP!' : 'JUMP');
    if (jump.root.dataset.state !== jumpState) jump.root.dataset.state = jumpState;

    const item = this.buttons.get('item')!;
    const hasItem = c.item !== ItemKind.NONE && c.charges > 0;
    toggle(item.root, 'empty', !hasItem);
    if (hasItem) {
      this.morph(item, `item:${c.item}`, '');
      const n = c.charges > 1 ? `×${Math.min(c.charges, 9)}` : '';
      if (this.badge.textContent !== n) {
        this.badge.textContent = n;
        this.badge.hidden = n === '';
        if (n !== '') this.flash(this.badge, 'got');
      }
    }
    if (hasItem && this.lastItem !== c.item) this.flash(item.root, 'got');
    this.lastItem = hasItem ? c.item : ItemKind.NONE;
  }

  /** Releases every held button and the stick (blur, mode change). */
  releaseAll(): void {
    for (const b of this.buttons.values()) {
      if (b.pointer !== null) this.releaseButton(b);
    }
    this.held.clear();
    this.endStick();
  }

  // ---- Layout ---------------------------------------------------------------------------------

  relayout(): void {
    if (!this.root) return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const layout = computeTouchLayout({
      width: w, height: h, safe: readSafeInsets(this.probe), uiScale: this.opts.uiScale, buttonSize: this.opts.buttonSize,
      leftHanded: this.opts.layout === 'left', killFeedHeight: this.killFeedHeight,
    });
    this.layoutValue = layout;
    this.root.dataset.orientation = layout.orientation;
    toggle(this.root, 'tc-mirrored', layout.mirrored);
    const z = layout.stickZone;
    place(this.zone, z.x, z.y, z.w, z.h);
    for (const b of this.buttons.values()) placeButton(b, layout.buttons[b.id]);
    const d = layout.stickRadius * 2;
    this.stick.style.setProperty('--sd', `${d}px`);
    this.stick.style.setProperty('--kd', `${layout.knobRadius * 2}px`);
    if (this.stickPointer === null) this.homeStick();
    this.positionRadial(this.emoteRadial, layout, 4);
    this.positionRadial(this.itemRadial, layout, 6);
    this.publishHud();
  }

  /**
   * HUD avoidance without JS: while controls show, the UI root carries `data-touch-mode` and
   * CSS custom properties (px) that HUD styles can use with fallbacks, e.g.
   * `bottom: var(--tc-dial-bottom, 1em)`. Removed when the controls hide.
   *   --tc-dial-x / --tc-dial-bottom / --tc-dial-max-w   bottom-center widget (Dizzy Dial)
   *   --tc-top-left-x                                     left edge for the top-left widget
   *   --tc-cluster-top / --tc-cluster-x0 / --tc-cluster-x1  the action cluster's box (either side)
   *   --tc-stick-x0 / --tc-stick-x1 / --tc-stick-top      the idle stick's box (also in the menu)
   *   --tc-top-row-x0 / --tc-top-row-top / --tc-top-row-bottom  HONK/EMOTE (+ item wheel) row
   */
  private publishHud(): void {
    const o = this.overlay;
    if (!o) return;
    const L = this.root && this.modeValue !== 'hidden' ? this.layoutValue : null;
    const full = this.modeValue === 'full';
    const vars = new Map<string, number>();
    if (L !== null) {
      vars.set('--tc-stick-x0', L.stickHome.x - L.stickRadius).set('--tc-stick-x1', L.stickHome.x + L.stickRadius).set('--tc-stick-top', L.stickHome.y - L.stickRadius);
      if (full) {
        vars.set('--tc-dial-x', L.hud.bottomCenter.cx).set('--tc-dial-bottom', L.hud.bottomCenter.bottom).set('--tc-dial-max-w', L.hud.bottomCenter.maxWidth)
          .set('--tc-top-left-x', L.hud.topLeftX).set('--tc-cluster-top', L.cluster.y)
          .set('--tc-cluster-x0', L.cluster.x).set('--tc-cluster-x1', L.cluster.x + L.cluster.w);
        const row = this.ctx.itemWheel ? L.buttons.items : L.buttons.emote;
        vars.set('--tc-top-row-x0', row.x - row.hit).set('--tc-top-row-top', L.topCluster.y).set('--tc-top-row-bottom', L.topCluster.y + L.topCluster.h);
      }
    }
    for (const name of HUD_VARS) {
      const v = vars.get(name);
      if (v !== undefined) o.style.setProperty(name, `${v.toFixed(1)}px`);
      else o.style.removeProperty(name);
    }
    if (L === null) delete o.dataset.touchMode;
    else o.dataset.touchMode = this.modeValue;
  }

  // ---- Buttons --------------------------------------------------------------------------------

  private makeButton(id: TouchButtonId, parent: HTMLElement): ButtonEl {
    const root = div(`tc-btn tc-${id}`, parent);
    root.dataset.touchBtn = id;
    const disc = div('tc-disc', root);
    const icon = div('tc-icon', disc);
    const label = div('tc-label', disc);
    const b: ButtonEl = { id, root, disc, icon, label, pointer: null, iconKey: '', labelText: '' };
    if (id === 'shove') {
      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('class', 'tc-ring');
      svg.setAttribute('viewBox', '0 0 100 100');
      const track = document.createElementNS(SVG_NS, 'circle');
      const ring = document.createElementNS(SVG_NS, 'circle');
      for (const c of [track, ring]) {
        c.setAttribute('cx', '50');
        c.setAttribute('cy', '50');
        c.setAttribute('r', String(RING_R));
      }
      track.setAttribute('class', 'tc-ring-track');
      ring.setAttribute('class', 'tc-ring-fill');
      ring.setAttribute('stroke-dasharray', RING_LEN.toFixed(1));
      ring.setAttribute('stroke-dashoffset', RING_LEN.toFixed(1));
      svg.append(track, ring);
      disc.prepend(svg);
      div('tc-sweep', disc);
      this.ring = ring;
      this.shoveDisc = disc;
    }
    if (id === 'item') {
      this.badge = div('tc-badge', disc);
      this.badge.hidden = true;
    }
    const initial: Record<TouchButtonId, [TouchIconId, string]> = {
      shove: ['bonk', 'BONK'], jump: ['jump', 'JUMP'], grab: ['grab', 'GRAB'], item: ['items', ''], honk: ['honk', ''],
      emote: ['emote', ''], pause: ['pause', ''], items: ['items', ''],
    };
    const [ic, lb] = initial[id];
    this.morph(b, ic, lb);
    const signal = this.abort.signal;
    root.addEventListener('pointerdown', (e) => this.onButtonDown(b, e), { signal });
    root.addEventListener('pointermove', (e) => this.onButtonMove(b, e), { signal });
    for (const t of ['pointerup', 'pointercancel', 'lostpointercapture'] as const) {
      root.addEventListener(t, (e) => {
        if (e.pointerId === b.pointer) this.onButtonUp(b, e);
      }, { signal });
    }
    return b;
  }

  private morph(b: ButtonEl, iconKey: string, label: string): void {
    if (b.iconKey !== iconKey) {
      b.iconKey = iconKey;
      b.icon.innerHTML = iconKey.startsWith('item:') ? ITEM_ICONS[Number(iconKey.slice(5))] ?? '' : TOUCH_ICONS[iconKey as TouchIconId] ?? '';
      if (this.root) this.flash(b.root, 'morph');
    }
    if (b.labelText !== label) {
      b.labelText = label;
      b.label.textContent = label;
      b.label.hidden = label === '';
    }
  }

  private onButtonDown(b: ButtonEl, e: PointerEvent): void {
    e.preventDefault();
    e.stopPropagation();
    this.hooks.activity();
    if (b.pointer !== null) return;
    b.pointer = e.pointerId;
    try {
      b.root.setPointerCapture(e.pointerId);
    } catch {
      // Synthetic pointers (tests) may not be capturable.
    }
    b.root.classList.add('down');
    this.ripple(b.root);
    switch (b.id) {
      case 'shove': case 'grab': case 'jump': case 'item': case 'honk':
        this.held.add(b.id);
        this.hooks.press(b.id);
        if (b.id === 'shove' && this.ctx.cooldown > 0.02) this.flash(b.root, 'nope');
        break;
      case 'pause':
        this.hooks.pause();
        break;
      case 'emote':
        this.toggleRadial(this.emoteRadial, e);
        break;
      case 'items':
        this.toggleRadial(this.itemRadial, e);
        break;
    }
  }

  private onButtonMove(b: ButtonEl, e: PointerEvent): void {
    if (e.pointerId !== b.pointer || this.radialPointer !== e.pointerId || !this.openRadial) return;
    this.hoverRadial(e.clientX, e.clientY);
  }

  private onButtonUp(b: ButtonEl, e: PointerEvent): void {
    if (this.radialPointer === e.pointerId && this.openRadial) {
      const r = this.openRadial;
      const i = this.radialIndexAt(r, e.clientX, e.clientY);
      this.radialPointer = null;
      if (i >= 0) {
        this.flash(r.items[i], 'got');
        r.pick(i);
        this.closeRadial();
      } else {
        // A tap: the wheel stays open for a second tap on an item (or the backdrop to close).
        this.radialSticky = true;
      }
    }
    this.releaseButton(b);
  }

  private releaseButton(b: ButtonEl): void {
    const id = b.id;
    b.pointer = null;
    b.root.classList.remove('down');
    if (id === 'shove' || id === 'grab' || id === 'jump' || id === 'item' || id === 'honk') {
      if (this.held.delete(id)) this.hooks.release(id);
    }
  }

  // ---- Radials (emote wheel, practice item wheel) ----------------------------------------------

  private makeRadial(parent: HTMLElement, source: TouchButtonId, cls: string, caption: string, icons: readonly string[], labels: readonly string[], pick: (i: number) => void): Radial {
    const root = div(`tc-radial ${cls}`, parent);
    root.hidden = true;
    const plate = div('tc-radial-plate', root);
    div('tc-radial-caption', plate).textContent = caption;
    const hub = div('tc-radial-hub', root);
    hub.innerHTML = RADIAL_CLOSE;
    hub.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.closeRadial();
    }, { signal: this.abort.signal });
    const items = icons.map((icon, i) => {
      const it = div('tc-radial-item', root);
      it.dataset.index = String(i);
      const disc = div('tc-disc', it);
      div('tc-icon', disc).innerHTML = icon;
      const lb = div('tc-label', disc);
      lb.textContent = labels[i];
      it.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.hooks.activity();
        pick(i);
        this.flash(it, 'got');
        this.closeRadial();
      }, { signal: this.abort.signal });
      return it;
    });
    // Tapping the dimmed backdrop closes the wheel.
    root.addEventListener('pointerdown', (e) => {
      if (e.target === root || e.target === plate) this.closeRadial();
    }, { signal: this.abort.signal });
    return { root, plate, items, source, cx: 0, cy: 0, pick };
  }

  /**
   * Wheels open in the free play area (between the stick and the cluster in landscape, above
   * them in portrait), never over the buttons. A swipe from the button picks by direction, so the
   * wheel's position does not matter for the gesture.
   */
  private positionRadial(r: Radial | null, layout: TouchLayout, n: number): void {
    if (!r) return;
    const W = window.innerWidth;
    const H = window.innerHeight;
    const ring = n > 4 ? 82 : 70;
    const reach = ring + 40;
    const cx = layout.orientation === 'landscape' ? layout.hud.bottomCenter.cx : W / 2;
    const cy = layout.orientation === 'landscape' ? H * 0.5 : Math.min(H * 0.46, H - layout.hud.bottomCenter.bottom - reach - 8);
    r.cx = Math.max(reach + 8, Math.min(W - reach - 8, cx));
    r.cy = Math.max(reach + 8, Math.min(H - reach - 8, cy));
    r.root.style.setProperty('--cx', `${r.cx}px`);
    r.root.style.setProperty('--cy', `${r.cy}px`);
    r.root.style.setProperty('--plate', `${(reach + 6) * 2}px`);
    r.items.forEach((it, i) => {
      // Emotes follow the D-pad (§8.2): up wave, right dance, down laugh, left flex.
      const a = n === 4 ? [-90, 0, 90, 180][i] * (Math.PI / 180) : -Math.PI / 2 + (i / n) * Math.PI * 2;
      it.style.setProperty('--ix', `${(Math.cos(a) * ring).toFixed(1)}px`);
      it.style.setProperty('--iy', `${(Math.sin(a) * ring).toFixed(1)}px`);
    });
  }

  private toggleRadial(r: Radial | null, e: PointerEvent): void {
    if (!r) return;
    if (this.openRadial === r) {
      this.closeRadial();
      return;
    }
    this.closeRadial();
    this.openRadial = r;
    this.radialPointer = e.pointerId;
    this.radialPress.x = e.clientX;
    this.radialPress.y = e.clientY;
    this.radialSticky = false;
    this.radialHover = -1;
    r.root.hidden = false;
    r.root.classList.remove('open');
    void r.root.offsetWidth;
    r.root.classList.add('open');
    this.buttons.get(r.source)?.root.classList.add('open');
  }

  private closeRadial(): void {
    const r = this.openRadial;
    if (!r) return;
    r.root.hidden = true;
    r.root.classList.remove('open');
    for (const it of r.items) it.classList.remove('hover');
    this.buttons.get(r.source)?.root.classList.remove('open');
    this.openRadial = null;
    this.radialPointer = null;
    this.radialSticky = false;
    this.radialHover = -1;
  }

  /** The wedge a swipe from the press point to (x, y) points at, or −1 for a tap. */
  private radialIndexAt(r: Radial, x: number, y: number): number {
    const dx = x - this.radialPress.x;
    const dy = y - this.radialPress.y;
    if (Math.hypot(dx, dy) < RADIAL_SELECT_PX) return -1;
    const n = r.items.length;
    const a = Math.atan2(dy, dx);
    if (n === 4) {
      const deg = ((a * 180) / Math.PI + 360) % 360;
      // up(270) wave=0, right(0) dance=1, down(90) laugh=2, left(180) flex=3
      if (deg >= 225 && deg < 315) return 0;
      if (deg >= 315 || deg < 45) return 1;
      if (deg >= 45 && deg < 135) return 2;
      return 3;
    }
    const t = (a + Math.PI / 2 + Math.PI * 2) % (Math.PI * 2);
    return Math.round(t / ((Math.PI * 2) / n)) % n;
  }

  private hoverRadial(x: number, y: number): void {
    const r = this.openRadial;
    if (!r) return;
    const i = this.radialIndexAt(r, x, y);
    if (i === this.radialHover) return;
    this.radialHover = i;
    r.items.forEach((it, k) => it.classList.toggle('hover', k === i));
  }

  // ---- Stick ----------------------------------------------------------------------------------

  private onStickDown(e: PointerEvent): void {
    e.preventDefault();
    this.hooks.activity();
    if (this.openRadial && this.radialSticky) this.closeRadial();
    if (this.ctx.grabbed && this.modeValue === 'full') {
      // While grabbed every tap is a mash (§8.3); the stick still wiggles too.
      this.hooks.press('grab');
      this.hooks.release('grab');
    }
    if (this.stickPointer !== null || !this.layoutValue) return;
    this.stickPointer = e.pointerId;
    try {
      this.zone.setPointerCapture(e.pointerId);
    } catch {
      // Synthetic pointers (tests) may not be capturable.
    }
    const L = this.layoutValue;
    if (this.opts.stick === 'fixed') {
      this.stickOrigin.x = L.stickHome.x;
      this.stickOrigin.y = L.stickHome.y;
    } else {
      // Spawn under the thumb, kept fully on screen.
      const r = L.stickRadius;
      this.stickOrigin.x = Math.max(r + 4, Math.min(window.innerWidth - r - 4, e.clientX));
      this.stickOrigin.y = Math.max(r + 4, Math.min(window.innerHeight - r - 4, e.clientY));
    }
    this.stick.classList.remove('idle');
    this.stick.classList.add('active');
    this.updateStick(e.clientX, e.clientY);
  }

  private onStickMove(e: PointerEvent): void {
    if (e.pointerId !== this.stickPointer) return;
    e.preventDefault();
    this.updateStick(e.clientX, e.clientY);
  }

  private onStickUp(e: PointerEvent): void {
    if (e.pointerId !== this.stickPointer) return;
    this.endStick();
  }

  private updateStick(x: number, y: number): void {
    const L = this.layoutValue;
    if (!L) return;
    const r = L.stickRadius;
    let dx = x - this.stickOrigin.x;
    let dy = y - this.stickOrigin.y;
    const m = Math.hypot(dx, dy);
    if (this.opts.stick === 'floating' && m > r * STICK_FOLLOW) {
      // Drag the base along so direction changes stay instant.
      const k = (m - r * STICK_FOLLOW) / m;
      this.stickOrigin.x += dx * k;
      this.stickOrigin.y += dy * k;
      dx = x - this.stickOrigin.x;
      dy = y - this.stickOrigin.y;
    }
    stickVector(dx, dy, r, this.move);
    const km = Math.min(1, Math.hypot(dx, dy) / r);
    const kx = m > 0 ? (dx / Math.max(1e-6, Math.hypot(dx, dy))) * km * r : 0;
    const ky = m > 0 ? (dy / Math.max(1e-6, Math.hypot(dx, dy))) * km * r : 0;
    this.stick.style.transform = `translate3d(${this.stickOrigin.x.toFixed(1)}px, ${this.stickOrigin.y.toFixed(1)}px, 0)`;
    this.knob.style.transform = `translate3d(${kx.toFixed(1)}px, ${ky.toFixed(1)}px, 0)`;
    const on = this.move.x !== 0 || this.move.y !== 0;
    toggle(this.stick, 'pushing', on);
    if (on) this.dir.style.transform = `rotate(${Math.atan2(dy, dx).toFixed(3)}rad)`;
  }

  private endStick(): void {
    this.stickPointer = null;
    this.move.x = 0;
    this.move.y = 0;
    if (!this.stick) return;
    this.stick.classList.remove('active', 'pushing');
    this.stick.classList.add('idle');
    this.homeStick();
  }

  private homeStick(): void {
    const L = this.layoutValue;
    if (!L || !this.stick) return;
    this.stick.style.transform = `translate3d(${L.stickHome.x.toFixed(1)}px, ${L.stickHome.y.toFixed(1)}px, 0)`;
    this.knob.style.transform = 'translate3d(0px, 0px, 0)';
  }

  // ---- Juice ----------------------------------------------------------------------------------

  private ripple(btn: HTMLElement): void {
    if (this.opts.reducedMotion) return;
    const r = div('tc-ripple', btn);
    r.addEventListener('animationend', () => r.remove(), { once: true });
    setTimeout(() => r.remove(), 600);
  }

  private burst(x: number, y: number): void {
    if (!this.root || this.opts.reducedMotion) return;
    const b = div('tc-burst', this.root);
    b.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    setTimeout(() => b.remove(), 450);
  }

  private flash(node: HTMLElement, kind: string): void {
    const cls = `fx-${kind}`;
    node.classList.remove(cls);
    void node.offsetWidth;
    node.classList.add(cls);
    setTimeout(() => node.classList.remove(cls), 650);
  }
}

function toggle(node: HTMLElement, cls: string, on: boolean): void {
  if (node.classList.contains(cls) !== on) node.classList.toggle(cls, on);
}

function place(node: HTMLElement, x: number, y: number, w: number, h: number): void {
  node.style.left = `${x.toFixed(1)}px`;
  node.style.top = `${y.toFixed(1)}px`;
  node.style.width = `${w.toFixed(1)}px`;
  node.style.height = `${h.toFixed(1)}px`;
}

function placeButton(b: ButtonEl, c: TouchCircle): void {
  place(b.root, c.x - c.hit, c.y - c.hit, c.hit * 2, c.hit * 2);
  b.root.style.setProperty('--d', `${(c.r * 2).toFixed(1)}px`);
}
